import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api";

/**
 * Reached via the link in the forgot-password email (or an admin's
 * "Generate reset link", Settings > Tenants) -- `?token=` carries the raw
 * single-use token; the API only ever sees/stores its sha256 hash. On
 * success, redirects to /login with a success note (Login reads
 * `location.state.note`).
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("missing reset token -- use the link from your email");
      return;
    }
    if (password !== confirm) {
      setError("passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post<{ ok: boolean }>("/api/auth/reset-password", { token, password });
      navigate("/login", {
        replace: true,
        state: { note: "Password reset. You can now sign in with your new password." },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="card login-card">
        <h1>Reset password</h1>
        {!token && (
          <div className="error-text">
            No reset token found in the link. Use the link from your email, or ask the
            administrator for a new one.
          </div>
        )}
        <form className="modal-body" onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="pw">New password</label>
            <input
              id="pw"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pw2">Confirm password</label>
            <input
              id="pw2"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          {error && <div className="error-text">{error}</div>}
          <button className="primary" type="submit" disabled={busy || !token}>
            {busy ? "Resetting..." : "Reset password"}
          </button>
          <div className="muted">
            <Link to="/login">Back to sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
